"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SyncHotkeyIconsResponse = exports.HotkeyIconUpload = exports.HotkeyIconAdmin = exports.HotkeyIcon = exports.HOTKEY_ICON_SIZES = exports.HOTKEY_ICON_CAP = exports.HOTKEY_ICON_SVG_MAX_BYTES = exports.HotkeyIconName = exports.HotkeyIconSlug = exports.HOTKEY_ICON_PREFIX = void 0;
exports.hotkeyIconName = hotkeyIconName;
exports.isCustomHotkeyIconName = isCustomHotkeyIconName;
exports.hotkeyIconSlugOf = hotkeyIconSlugOf;
const zod_1 = require("zod");
/**
 * Custom hotkey icons (VL-260).
 *
 * ------------------------------------------------------------------
 * WHY A HOTKEY ICON IS DELIVERED AND NOT FETCHED
 * ------------------------------------------------------------------
 * A hotkey reaches the till as a `CatalogHotkeyNode` carrying `iconName` — a
 * NAME, resolved against an icon set the terminal already ships in its binary.
 * It has never carried an image, and terminals deliberately do not pull
 * arbitrary assets at runtime.
 *
 * So "upload an SVG on Platform" had nowhere to land. The delivery route was
 * settled on VL-260: *"deliver the asset through sync, similarly to
 * translations and catalogue data, by introducing a new content type"*. That is
 * this file. A custom icon is a small piece of authored content that rides the
 * sync feed like a translation string, not a binary a lane goes and fetches.
 *
 * ------------------------------------------------------------------
 * WHY THE SLUG IS NAMESPACED
 * ------------------------------------------------------------------
 * `iconName` is a flat string shared with the terminal's BUILT-IN icon set,
 * and BOS does not know what is in that set. An unprefixed custom slug could
 * therefore silently shadow a shipped glyph — or be shadowed by one — and
 * which of the two a till rendered would depend on its build.
 *
 * `custom:` removes the ambiguity in one character class: a name with the
 * prefix is resolved against the synced icons and nothing else, a name without
 * it against the built-in set and nothing else. Neither can mask the other.
 */
/** The `custom:` namespace that separates an authored icon from a shipped one. */
exports.HOTKEY_ICON_PREFIX = 'custom:';
/**
 * The slug inside the namespace. Lowercase, digits and dashes only: it travels
 * in a JSON field, appears in an audit trail and is typed by hand into the
 * picker, so the character set is kept boring on purpose.
 */
exports.HotkeyIconSlug = zod_1.z
    .string()
    .min(1)
    .max(56)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits and single dashes only');
/** The full `iconName` a hotkey node carries for a custom icon. */
exports.HotkeyIconName = zod_1.z
    .string()
    .min(exports.HOTKEY_ICON_PREFIX.length + 1)
    .max(64)
    .refine((value) => value.startsWith(exports.HOTKEY_ICON_PREFIX), {
    message: `a custom icon name must start with "${exports.HOTKEY_ICON_PREFIX}"`,
});
function hotkeyIconName(slug) {
    return `${exports.HOTKEY_ICON_PREFIX}${slug}`;
}
function isCustomHotkeyIconName(iconName) {
    return typeof iconName === 'string' && iconName.startsWith(exports.HOTKEY_ICON_PREFIX);
}
function hotkeyIconSlugOf(iconName) {
    return iconName.startsWith(exports.HOTKEY_ICON_PREFIX) ? iconName.slice(exports.HOTKEY_ICON_PREFIX.length) : null;
}
/**
 * Ceiling on one normalised icon, in bytes of UTF-8 markup.
 *
 * A hotkey glyph is a handful of paths; 32 KB is generous for that and small
 * enough that the whole set rides one sync page. The cap exists because these
 * are delivered INLINE: without it, one 4 MB traced-photograph "icon" would be
 * pushed to every till on the estate on the next poll.
 */
exports.HOTKEY_ICON_SVG_MAX_BYTES = 32 * 1024;
/** How many custom icons one tenant may hold. */
exports.HOTKEY_ICON_CAP = 256;
/**
 * Render sizes from the terminal design prototype, recorded on VL-260.
 *
 * The ticket asks Platform to "convert / resize the uploaded icon to the size
 * defined by the terminals design mockup". For a VECTOR the correct conversion
 * is not to bake in a pixel size — it is to remove the fixed one, so a single
 * stored icon renders crisply at every size below. That is what normalisation
 * does: the `viewBox` is preserved (or synthesised from the declared width and
 * height) and the fixed `width`/`height` attributes are dropped.
 *
 * The table is carried here so the two repositories agree on the numbers, and
 * so an author can be told what their upload will be drawn at.
 */
exports.HOTKEY_ICON_SIZES = {
    /** Quick Actions tile: 62 px tall, 30×30 container at 9 px radius. */
    quickAction: { container: 30, svg: 17 },
    /** Catalog buttons directly below Quick Actions — same container. */
    catalog: { container: 30, svg: 17 },
    /** Hot / Enter code, in the 44 px scan row. No container. */
    hot: { container: null, svg: 19 },
    /** Product tiles in the full-screen catalog. */
    productTile: { container: 34, svg: 19 },
    /** Modal container. */
    modal: { container: 42, svg: 22 },
    /** Note container. */
    note: { container: 26, svg: 15 },
};
/**
 * One custom icon as the terminal receives it.
 *
 * `svg` is normalised, sanitised markup — see `HOTKEY_ICON_SIZES` for what
 * normalisation means for a vector, and the platform's `normaliseHotkeyIconSvg`
 * for what sanitisation strips.
 */
exports.HotkeyIcon = zod_1.z.object({
    slug: exports.HotkeyIconSlug,
    /** Operator-facing label. Never rendered on the till. */
    name: zod_1.z.string().min(1).max(120),
    svg: zod_1.z.string().min(1).max(exports.HOTKEY_ICON_SVG_MAX_BYTES),
    /** SHA-256 of `svg`, so a terminal can skip an unchanged icon. */
    contentHash: zod_1.z.string().length(64),
    updatedAt: zod_1.z.string().datetime(),
});
/** Admin read shape — adds how many hotkeys currently reference the icon. */
exports.HotkeyIconAdmin = exports.HotkeyIcon.extend({
    usageCount: zod_1.z.number().int().min(0),
});
exports.HotkeyIconUpload = zod_1.z.object({
    slug: exports.HotkeyIconSlug,
    name: zod_1.z.string().min(1).max(120),
    /** Raw `.svg` markup as uploaded. Normalised server-side before storage. */
    svg: zod_1.z
        .string()
        .min(1)
        .max(exports.HOTKEY_ICON_SVG_MAX_BYTES * 4),
});
/**
 * `GET /sync/hotkey-icons` — a full snapshot, like the hotkey tree itself.
 *
 * Snapshot rather than a delta: the whole set is a few tens of kilobytes, and
 * a partial icon set is worse than none — a till holding half the icons draws
 * fallback glyphs on buttons that look authored. `version` supports the same
 * `?since=` + 304 fast path the hotkey tree uses, so an unchanged set costs one
 * empty response.
 */
exports.SyncHotkeyIconsResponse = zod_1.z.object({
    version: zod_1.z.number().int().min(1),
    icons: zod_1.z.array(exports.HotkeyIcon).max(exports.HOTKEY_ICON_CAP),
    snapshotAt: zod_1.z.string().datetime(),
});
//# sourceMappingURL=hotkey-icons.js.map